import React from "react";
import { 
  Grid, 
  Paper, 
  Typography, 
  Box, 
  Chip, 
  Divider 
} from "@mui/material";
import { format } from "date-fns";
import colors from "../../../config/colors";
import CustomLabel from "../common/customLabel";
import UseIconHooks from "../../hooks/useIconHooks";
import { useSelector } from "react-redux";

const AutorizacionesList = ({ 
  autorizaciones = [], 
  loading = false, 
  title = "Historial de Autorizaciones de Medicamentos",
  customStyles = {},
  onAutorizacionClick // Nueva prop para manejar el click
}) => {
  const infoAfiliado = useSelector((state) => state.afiliado.infoAfiliado);
  const isDark = infoAfiliado.isUltra && infoAfiliado.ultraDarkMode;
  const isUltraLight = infoAfiliado.isUltra && !infoAfiliado.ultraDarkMode;

  const defaultStyles = {
    container: {
      marginTop: "24px",
      ...customStyles.container,
    },
    title: {
      fontSize: "20px",
      fontWeight: "600",
      color: isDark ? colors.ultraAccentLight : isUltraLight ? colors.ulText : colors.primary,
      marginBottom: "16px",
      ...customStyles.title,
    },
    paper: {
      padding: "20px",
      borderRadius: "12px",
      marginBottom: "16px",
      backgroundColor: isDark ? colors.ultraMid : isUltraLight ? colors.ulCard : undefined,
      border: isDark
        ? `1px solid ${colors.ultraAccentBorder}`
        : isUltraLight
        ? `1px solid ${colors.ulBorder}`
        : `1px solid ${colors.primary}20`,
      transition: "all 0.3s ease",
      cursor: onAutorizacionClick ? "pointer" : "default",
      "&:hover": {
        transform: "translateY(-2px)",
        boxShadow: isDark
          ? "0 4px 20px rgba(0,0,0,0.5)"
          : isUltraLight
          ? "0 4px 16px rgba(0,0,0,0.10)"
          : "0 4px 20px rgba(0,0,0,0.1)",
        borderColor: isDark ? colors.ultraAccent : isUltraLight ? colors.ulAccentSoft : `${colors.primary}40`,
      },
      ...customStyles.paper,
    },
    authHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "12px",
    },
    authNumber: {
      fontSize: "18px",
      fontWeight: "700",
      color: isDark ? colors.ultraAccentLight : isUltraLight ? colors.ulText : colors.primary,
    },
    statusChip: {
      fontWeight: "600",
      textTransform: "uppercase",
      fontSize: "12px",
    },
    infoGrid: {
      marginTop: "12px",
    },
    infoLabel: {
      fontSize: "12px",
      fontWeight: "600",
      color: isDark ? colors.ultraAccentLight : isUltraLight ? colors.ulTextSec : colors.gray,
      textTransform: "uppercase",
      letterSpacing: "0.5px",
      marginBottom: "4px",
    },
    infoValue: {
      fontSize: "14px",
      fontWeight: "500",
      color: isDark ? colors.ultraSilver : isUltraLight ? colors.ulText : "#333",
      wordBreak: "break-word",
    },
    emptyState: {
      textAlign: "center",
      padding: "40px 20px",
      color: colors.gray,
    },
    loadingState: {
      textAlign: "center",
      padding: "40px 20px",
      color: colors.primary,
    },
  };

  const getStatusColor = (estado) => {
    const statusColors = {
      "Autorizada": "#4caf50",
      "Pendiente": "#ff9800", 
      "Rechazada": "#f44336",
      "En Proceso": "#2196f3",
      "Vencida": "#9e9e9e",
    };
    return statusColors[estado] || colors.gray;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-DO', {
      style: 'currency',
      currency: 'DOP',
    }).format(amount);
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), "dd/MM/yyyy");
    } catch (error) {
      return dateString;
    }
  };

  if (loading) {
    return (
      <Box sx={defaultStyles.container}>
        <Typography sx={defaultStyles.title}>
          {title}
        </Typography>
        <Box sx={defaultStyles.loadingState}>
          <Box display="flex" alignItems="center" justifyContent="center" gap={2}>
            {UseIconHooks("hourglass_empty", { 
              fontSize: "large", 
              style: { color: isDark ? colors.ultraAccentLight : isUltraLight ? colors.ulText : colors.primary } 
            })}
            <Typography variant="h6" style={{ color: isDark ? colors.ultraAccentLight : isUltraLight ? colors.ulText : colors.primary }}>
              Cargando autorizaciones...
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  if (!autorizaciones || autorizaciones.length === 0) {
    return (
      <Box sx={defaultStyles.container}>
        <Typography sx={defaultStyles.title}>
          {title}
        </Typography>
        <Paper sx={defaultStyles.paper}>
          <Box sx={defaultStyles.emptyState}>
            <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
              {UseIconHooks("medication", { 
                fontSize: "large", 
                style: { color: colors.gray, fontSize: "48px" } 
              })}
              <Typography variant="h6" style={{ color: colors.gray }}>
                No se encontraron autorizaciones
              </Typography>
              <Typography variant="body2" style={{ color: colors.gray }}>
                Aún no tienes autorizaciones de medicamentos registradas en el sistema.
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={defaultStyles.container}>
      <Typography sx={defaultStyles.title}>
        {title}
      </Typography>
      
      {autorizaciones.map((autorizacion) => (
        <Paper 
          key={autorizacion.idAuth} 
          sx={defaultStyles.paper}
          onClick={() => onAutorizacionClick && onAutorizacionClick(autorizacion)}
        >
          {/* Header con número de autorización y estado */}
          <Box sx={defaultStyles.authHeader}>
            <Box display="flex" alignItems="center" gap={1}>
              {UseIconHooks("verified", { 
                fontSize: "medium", 
                style: { color: colors.primary } 
              })}
              <Typography sx={defaultStyles.authNumber}>
                Autorización #{autorizacion.auth}
              </Typography>
              {onAutorizacionClick && (
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: colors.gray, 
                    fontStyle: "italic",
                    marginLeft: 1 
                  }}
                >
                  (Click para ver detalle)
                </Typography>
              )}
            </Box>
            <Chip
              label={autorizacion.estado}
              sx={{
                ...defaultStyles.statusChip,
                backgroundColor: getStatusColor(autorizacion.estado),
                color: "white",
              }}
            />
          </Box>

          <Divider sx={{ marginY: "12px" }} />

          {/* Información detallada */}
          <Grid container spacing={2} sx={defaultStyles.infoGrid}>
            <Grid item xs={12} md={4}>
              <Typography sx={defaultStyles.infoLabel}>
                Farmacia
              </Typography>
              <Typography sx={defaultStyles.infoValue}>
                {autorizacion.solicitanteDesc?.trim()}
              </Typography>
            </Grid>

            <Grid item xs={12} md={3}>
              <Typography sx={defaultStyles.infoLabel}>
                Fecha
              </Typography>
              <Typography sx={defaultStyles.infoValue}>
                {formatDate(autorizacion.fecha)}
              </Typography>
            </Grid>

            <Grid item xs={12} md={2}>
              <Typography sx={defaultStyles.infoLabel}>
                Hora
              </Typography>
              <Typography sx={defaultStyles.infoValue}>
                {autorizacion.fechaT}
              </Typography>
            </Grid>

            <Grid item xs={12} md={3}>
              <Typography sx={defaultStyles.infoLabel}>
                Monto Autorizado
              </Typography>
              <Typography 
                sx={{
                  ...defaultStyles.infoValue,
                  fontWeight: "700",
                  color: colors.primary,
                  fontSize: "16px",
                }}
              >
                {formatCurrency(autorizacion.montoAutorizado)}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      ))}
      
      {/* Footer informativo */}
      <Box 
        sx={{ 
          textAlign: "center", 
          marginTop: "16px", 
          padding: "12px",
          backgroundColor: colors.grayFondo,
          borderRadius: "8px",
        }}
      >
        <Typography variant="body2" style={{ color: colors.gray }}>
          Mostrando {autorizaciones.length} autorización{autorizaciones.length !== 1 ? 'es' : ''} de medicamentos
        </Typography>
      </Box>
    </Box>
  );
};

export default AutorizacionesList;